import type { PropertyDefinition } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Eye, GripVertical, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { RemoveOptionConfirmDialog } from "@/components/remove-option-confirm-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { usePropertyOptions } from "@/hooks/use-property-options";
import { NAMED_COLORS } from "@/lib/property-colors";
import { rpcClient } from "@/lib/rpc";

interface PropertyConfigSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobOpeningId: string;
  definitions: PropertyDefinition[];
  queryKey: unknown[];
}

export function PropertyConfigSheet({
  open,
  onOpenChange,
  jobOpeningId,
  definitions,
  queryKey,
}: PropertyConfigSheetProps) {
  const queryClient = useQueryClient();
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [deleteDefId, setDeleteDefId] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.createPropertyDefinition({ id: jobOpeningId });
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey });
      setEditingNameId(created.id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PropertyDefinition> }) => {
      return rpcClient.jobOpenings.updatePropertyDefinition({
        id: jobOpeningId,
        defId: id,
        ...data,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return rpcClient.jobOpenings.deletePropertyDefinition({ id: jobOpeningId, defId: id });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[560px] sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle>Propriedades do processo</SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-3 overflow-y-auto pr-1">
          {definitions.map((def) => (
            <div key={def.id} className="flex flex-col gap-2 p-3 border rounded-lg bg-card">
              {/* Row 1: drag handle + name */}
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-grab" />

                {editingNameId === def.id ? (
                  <Input
                    defaultValue={def.name}
                    autoFocus
                    className="h-7 flex-1 text-sm"
                    onBlur={(e) => {
                      updateMutation.mutate({ id: def.id, data: { name: e.target.value } });
                      setEditingNameId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        updateMutation.mutate({
                          id: def.id,
                          data: { name: e.currentTarget.value },
                        });
                        setEditingNameId(null);
                      }
                      if (e.key === "Escape") setEditingNameId(null);
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="flex-1 text-sm text-left hover:underline font-medium"
                    onClick={() => setEditingNameId(def.id)}
                  >
                    {def.name}
                  </button>
                )}
              </div>

              {/* Row 2: type selector + show-on-card toggle + delete */}
              <div className="flex items-center gap-2 pl-6">
                <Select
                  value={def.type}
                  onValueChange={(val) =>
                    updateMutation.mutate({
                      id: def.id,
                      data: { type: val as PropertyDefinition["type"] },
                    })
                  }
                >
                  <SelectTrigger className="h-7 flex-1 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto</SelectItem>
                    <SelectItem value="select">Seleção</SelectItem>
                    <SelectItem value="multi_select">Multi-seleção</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-1 flex-shrink-0" title="Mostrar no card">
                  <Eye className="h-3 w-3 text-muted-foreground" />
                  <Switch
                    checked={def.showOnCard}
                    onCheckedChange={(checked) =>
                      updateMutation.mutate({
                        id: def.id,
                        data: { showOnCard: checked },
                      })
                    }
                    className="scale-75"
                  />
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive flex-shrink-0"
                  onClick={() => setDeleteDefId(def.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>

              {/* Row 3: options editor for select / multi_select */}
              {(def.type === "select" || def.type === "multi_select") && (
                <OptionsEditor definition={def} queryKey={queryKey} />
              )}
            </div>
          ))}

          {definitions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma propriedade ainda. Clique em "Adicionar" para começar.
            </p>
          )}

          <Button
            variant="outline"
            className="w-full"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar propriedade
          </Button>
        </div>
      </SheetContent>

      <AlertDialog open={!!deleteDefId} onOpenChange={(open) => !open && setDeleteDefId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir propriedade?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá a propriedade e todos os seus valores em todos os candidatos deste
              processo. Não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteDefId) deleteMutation.mutate(deleteDefId);
                setDeleteDefId(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

// ─── Options editor (inline within each select/multi_select property card) ──

function OptionsEditor({
  definition,
  queryKey,
}: {
  definition: PropertyDefinition;
  queryKey: unknown[];
}) {
  const [newLabel, setNewLabel] = useState("");
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);

  const {
    options,
    addOption: addMutation,
    updateOption: updateMutation,
    deleteOption: deleteMutation,
    pendingRemoval,
    confirmRemoval,
    cancelRemoval,
  } = usePropertyOptions(definition, queryKey);

  return (
    <div className="pl-6 flex flex-col gap-1.5">
      <p className="text-xs text-muted-foreground font-medium">Opções</p>

      {options.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Nenhuma opção definida.</p>
      )}

      {options.map((opt) => (
        <div key={opt.id} className="flex items-center gap-1.5 group">
          <Badge
            variant="secondary"
            className="text-xs cursor-default"
            style={{ backgroundColor: opt.color }}
          >
            {opt.label}
          </Badge>
          <Popover
            open={editingOptionId === opt.id}
            onOpenChange={(isOpen) => setEditingOptionId(isOpen ? opt.id : null)}
          >
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 flex-shrink-0 opacity-0 group-hover:opacity-100"
              >
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" side="right" align="start">
              <Input
                key={opt.id}
                defaultValue={opt.label}
                autoFocus
                className="h-7 text-sm mb-2"
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && val !== opt.label) {
                    updateMutation.mutate({ optionId: opt.id, label: val });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = e.currentTarget.value.trim();
                    if (val && val !== opt.label) {
                      updateMutation.mutate({ optionId: opt.id, label: val });
                    }
                    setEditingOptionId(null);
                  }
                  if (e.key === "Escape") setEditingOptionId(null);
                }}
              />
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm text-destructive"
                onClick={() =>
                  deleteMutation.mutate(
                    { optionId: opt.id },
                    { onSuccess: () => setEditingOptionId(null) },
                  )
                }
              >
                <Trash2 className="h-3 w-3" />
                Excluir
              </button>
              <p className="text-xs text-muted-foreground px-2 mt-2 mb-1">Cores</p>
              {NAMED_COLORS.map((c) => (
                <button
                  type="button"
                  key={c.value}
                  className="flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm"
                  onClick={() => updateMutation.mutate({ optionId: opt.id, color: c.value })}
                >
                  <span
                    className="h-4 w-4 rounded-sm flex-shrink-0 border border-border"
                    style={{ backgroundColor: c.value }}
                  />
                  {c.name}
                  {opt.color === c.value && <Check className="h-3 w-3 ml-auto" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      ))}

      {/* Add new option */}
      <div className="flex gap-1 mt-0.5">
        <Input
          placeholder="Nova opção..."
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          className="h-7 text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter" && newLabel.trim()) {
              addMutation.mutate(newLabel.trim(), { onSuccess: () => setNewLabel("") });
            }
          }}
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 flex-shrink-0"
          disabled={!newLabel.trim() || addMutation.isPending}
          onClick={() => {
            if (newLabel.trim())
              addMutation.mutate(newLabel.trim(), { onSuccess: () => setNewLabel("") });
          }}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      <RemoveOptionConfirmDialog
        pending={pendingRemoval}
        onConfirm={confirmRemoval}
        onCancel={cancelRemoval}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
